import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../constants";
import { track } from "../utils/analytics";
import { authHeader } from "../utils/auth";

const SET_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What a photo link gives away, said once wherever an owner can make one. */
export const TOUCHUP_SHARE_NOTE =
  "Anyone with a photo link can view and download these photos, both the edited versions and the originals, but not your listing prep. You can turn photo links off at any time; photos already downloaded can't be recalled.";

/**
 * Accept only a link back to this set's gallery that carries a capability.
 * @param {unknown} value @param {string} setUuid
 */
const galleryShareUrl = (value, setUuid) => {
  if (typeof value !== "string" || value.length > 2048) return "";
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) &&
      url.pathname.toLowerCase() === `/touchups/${setUuid.toLowerCase()}` &&
      !!url.searchParams.get("share_token")
      ? value
      : "";
  } catch {
    return "";
  }
};

/**
 * Owner controls for a photos-only link to one touch-up set (Trello #91). The
 * July hardening made the gallery owner-only, so a forwarded gallery URL 403s;
 * this link is the revocable replacement. The link is a signed capability: it
 * is shown and copied once, held in memory only, and never logged, stored, or
 * sent to analytics. The server keeps no copy either, so turning links off
 * kills every photo link to the set at once. It does so by advancing the
 * listing prep's photo-access generation, which also retires the prep's own
 * outstanding photo URLs (owner and shared pages); those pages get fresh ones
 * on their next load, so there is nothing extra to tell the owner.
 *
 * @param {{setUuid:string, computationUuid?:string, variant:"gallery"|"share"}} options
 */
const useTouchupShareLink = ({ setUuid, computationUuid = "", variant }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(/** @type {"create"|"revoke"|null} */ (null));
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const activeRef = useRef(setUuid);
  activeRef.current = setUuid;
  const flightRef = useRef(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // A different set never inherits the previous set's link or messages.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the set identity is the reset key.
  useEffect(() => {
    flightRef.current = false;
    setUrl("");
    setBusy(null);
    setError("");
    setStatus("");
  }, [setUuid]);

  const context = useCallback(
    () => ({
      variant,
      ...(SET_UUID.test(computationUuid) ? { computation_uuid: computationUuid } : {}),
    }),
    [computationUuid, variant]
  );

  const create = useCallback(async () => {
    if (flightRef.current || !SET_UUID.test(setUuid)) return;
    flightRef.current = true;
    const requestUuid = setUuid;
    const current = () => mountedRef.current && activeRef.current === requestUuid;
    setBusy("create");
    setError("");
    setStatus("");
    try {
      const response = await axios.post(
        `${API_URL}/touchups/${encodeURIComponent(requestUuid)}/share-link/`,
        {},
        { headers: authHeader() }
      );
      if (!current()) return;
      const link = galleryShareUrl(response.data?.share_url, requestUuid);
      if (!link) {
        setError("The photo link was not returned. Try again.");
        return;
      }
      setUrl(link);
      track("touchups_share_link_created", context());
      try {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
        await navigator.clipboard.writeText(link);
        if (current()) setStatus("Photo link copied.");
      } catch {
        if (current()) setError("The link is ready below, but could not be copied automatically.");
      }
    } catch (failure) {
      if (!current()) return;
      // Status only, never backend text.
      const code = /** @type {{response?: {status?: number}}} */ (failure).response?.status;
      setError(
        code === 409
          ? "There are no edited photos to share yet."
          : code === 403
            ? "Photo links can't be created for this listing."
            : "Could not create a photo link. Check your connection, then try again."
      );
    } finally {
      if (current()) {
        flightRef.current = false;
        setBusy(null);
      }
    }
  }, [context, setUuid]);

  const revoke = useCallback(async () => {
    if (flightRef.current || !SET_UUID.test(setUuid)) return;
    flightRef.current = true;
    const requestUuid = setUuid;
    const current = () => mountedRef.current && activeRef.current === requestUuid;
    // Drop the shown link first: whatever the outcome, it is not to be copied again.
    setUrl("");
    setBusy("revoke");
    setError("");
    setStatus("");
    try {
      await axios.post(
        `${API_URL}/touchups/${encodeURIComponent(requestUuid)}/share-link/revoke/`,
        {},
        { headers: authHeader() }
      );
      if (!current()) return;
      // Only photo links die here. A gallery link inside a shared report (the
      // report-ready email, an admin resend) carries the report's own token
      // and lives until the report's public links are revoked (review 09-30).
      setStatus(
        "Photo links are turned off. A report you've shared can still show these photos until you use “Revoke all public links” on its Share page."
      );
      track("touchups_share_link_revoked", context());
    } catch {
      if (current())
        setError("Could not turn off photo links. Check your connection, then try again.");
    } finally {
      if (current()) {
        flightRef.current = false;
        setBusy(null);
      }
    }
  }, [context, setUuid]);

  // Another control already killed the links (the Share page's "Revoke all
  // public links" also retires photo links): stop showing a dead one.
  const forget = useCallback(() => {
    setUrl("");
    setError("");
    setStatus("");
  }, []);

  return { url, busy, error, status, create, revoke, forget };
};

export default useTouchupShareLink;
